import { call, takeEvery, takeLatest } from "redux-saga/effects";
import type { PayloadAction } from "@reduxjs/toolkit";

import { TemplatesDataConnector } from "../../api/templatesDataConnector";
import { templatesActions } from "./templatesSlice";
import type {
  GetTemplatesResponse,
  CreateDraftOperationResponse,
  CreateDraftRequest,
  PublishTemplateRequest,
  PublishTemplateOperationResponse,
  CloneTemplateRequest,
  CloneTemplateOperationResponse,
  ActivateTemplateVersionRequest,
  ActivateTemplateVersionResponse,
  RenameTemplateRequest,
  RenameTemplateOperationResponse,
  DeleteTemplateRequest,
  DeleteTemplateOperationResponse,
  GetTemplatePresignedUrlRequest,
  GetTemplatePresignedUrlResponse,
} from "./templatesTypes";
import { withErrorHandling } from "../utils/sagaHelpers";
import { createLogger } from "../../features/notifications/utils/logger";

const logger = createLogger("templatesSaga");

function* getTemplatesWorker(
  action: PayloadAction<{
    clientId?: string;
    platformId?: string;
    templateCategory?: "OPTUM" | "PLATFORM" | "CLIENT";
    token: string;
  }>,
) {
  const requestedCategory = action.payload.templateCategory;
  yield* withErrorHandling(
    "getTemplatesWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const data: GetTemplatesResponse = yield call(() =>
        dc.getTemplates(
          action.payload.clientId,
          action.payload.platformId,
          action.payload.templateCategory,
          action.payload.token,
        ),
      );
      return { data, templateCategory: requestedCategory };
    },
    templatesActions.getTemplatesSucceeded,
    templatesActions.getTemplatesFailed,
    "Failed to fetch templates",
  );
}

function* renameTemplateWorker(action: PayloadAction<RenameTemplateRequest>) {
  yield* withErrorHandling(
    "renameTemplateWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: RenameTemplateOperationResponse = yield call(() =>
        dc.renameTemplate(
          {
            templateId: action.payload.templateId,
            templateName: action.payload.templateName,
            templateFolder: action.payload.templateFolder,
            templateCategory: action.payload.templateCategory,
            platformId: action.payload.platformId,
            platformClientsId: action.payload.platformClientsId,
          },
          action.payload.token,
        ),
      );
      return { result };
    },
    templatesActions.renameTemplateSucceeded,
    templatesActions.renameTemplateFailed,
    "Failed to rename template",
  );
}

function* deleteTemplateWorker(action: PayloadAction<DeleteTemplateRequest>) {
  yield* withErrorHandling(
    "deleteTemplateWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: DeleteTemplateOperationResponse = yield call(() =>
        dc.deleteTemplate(
          {
            templateRowId: action.payload.templateRowId,
            templateCategory: action.payload.templateCategory,
            platformId: action.payload.platformId,
            platformClientsId: action.payload.platformClientsId,
          },
          action.payload.token,
        ),
      );
      return { result };
    },
    templatesActions.deleteTemplateSucceeded,
    templatesActions.deleteTemplateFailed,
    "Failed to delete template",
  );
}

function* publishTemplateWorker(action: PayloadAction<PublishTemplateRequest>) {
  yield* withErrorHandling(
    "publishTemplateWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: PublishTemplateOperationResponse = yield call(() =>
        dc.publishTemplate(
          {
            platformClientsId: action.payload.platformClientsId ?? "",
            platformId: action.payload.platformId ?? "",
            templateId: action.payload.templateId,
          },
          action.payload.token,
        ),
      );
      return { result };
    },
    templatesActions.publishTemplateSucceeded,
    templatesActions.publishTemplateFailed,
    "Failed to publish template",
  );
}

function* cloneTemplateWorker(action: PayloadAction<CloneTemplateRequest>) {
  yield* withErrorHandling(
    "cloneTemplateWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: CloneTemplateOperationResponse = yield call(() =>
        dc.cloneTemplate(
          {
            templateName: action.payload.templateName,
            templateFolder: action.payload.templateFolder,
            templateCategory: action.payload.templateCategory,
            platformClientsId: action.payload.platformClientsId,
            platformId: action.payload.platformId,
            templateVersionId: action.payload.templateVersionId,
            templateId: action.payload.templateId,
          },
          action.payload.token,
        ),
      );
      return { result };
    },
    templatesActions.cloneTemplateSucceeded,
    templatesActions.cloneTemplateFailed,
    "Failed to clone template",
  );
}

function* createDraftWorker(action: PayloadAction<CreateDraftRequest>) {
  yield* withErrorHandling(
    "createDraftWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: CreateDraftOperationResponse = yield call(() =>
        dc.createDraft(
          {
            templateName: action.payload.templateName,
            templateFolder: action.payload.templateFolder,
            templateCategory: action.payload.templateCategory,
            platformClientsId: action.payload.platformClientsId,
            platformId: action.payload.platformId,
            templateVersionId: action.payload.templateVersionId,
            templateId: action.payload.templateId,
          },
          action.payload.token,
        ),
      );
      return { result };
    },
    templatesActions.createDraftSucceeded,
    templatesActions.createDraftFailed,
    "Failed to create draft",
  );
}

function* activateTemplateVersionWorker(
  action: PayloadAction<ActivateTemplateVersionRequest>,
) {
  yield* withErrorHandling(
    "activateTemplateVersionWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const result: ActivateTemplateVersionResponse = yield call(() =>
        dc.activateTemplateVersion(
          action.payload.templateRowId,
          action.payload.templateVersionRowId,
          action.payload.token,
          action.payload.action,
        ),
      );
      return { result };
    },
    templatesActions.activateTemplateVersionSucceeded,
    templatesActions.activateTemplateVersionFailed,
    "Failed to activate template version",
  );
}

function* fetchTemplatePreviewWorker(
  action: PayloadAction<{ cacheKey: string; src: string }>,
) {
  const { cacheKey } = action.payload;
  yield* withErrorHandling(
    "fetchTemplatePreviewWorker",
    function* () {
      const dc = new TemplatesDataConnector();
      const html: string = yield call(() =>
        dc.fetchTemplatePreviewHtml(cacheKey, action.payload.src),
      );
      return { cacheKey, html };
    },
    templatesActions.fetchTemplatePreviewSucceeded,
    templatesActions.fetchTemplatePreviewFailed,
    "Failed to load template preview",
    { cacheKey },
  );
}

function* getTemplatePresignedUrlWorker(
  action: PayloadAction<GetTemplatePresignedUrlRequest>,
) {
  const s3Path = action.payload.s3Path;
  yield* withErrorHandling(
    "getTemplatePresignedUrlWorker",
    function* () {
      logger.debug("[s3-presigned] Request →", { s3Path });
      const dc = new TemplatesDataConnector();
      const result: GetTemplatePresignedUrlResponse = yield call(() =>
        dc.getTemplatePresignedUrl(s3Path, action.payload.token),
      );
      logger.debug("[s3-presigned] Response ←", { s3Path, presignedUrl: result.presignedUrl });
      return { s3Path, result };
    },
    templatesActions.getTemplatePresignedUrlSucceeded,
    templatesActions.getTemplatePresignedUrlFailed,
    "Failed to fetch presigned URL",
    { s3Path },
  );
}

export function* templatesSaga() {
  // GET/list
  yield takeLatest(
    templatesActions.getTemplatesRequested.type,
    getTemplatesWorker,
  );

  // Mutations (don't cancel previous by default)
  yield takeEvery(
    templatesActions.renameTemplateRequested.type,
    renameTemplateWorker,
  );
  yield takeEvery(
    templatesActions.deleteTemplateRequested.type,
    deleteTemplateWorker,
  );
  yield takeEvery(
    templatesActions.createDraftRequested.type,
    createDraftWorker,
  );
  yield takeEvery(
    templatesActions.publishTemplateRequested.type,
    publishTemplateWorker,
  );
  yield takeEvery(
    templatesActions.cloneTemplateRequested.type,
    cloneTemplateWorker,
  );
  yield takeEvery(
    templatesActions.activateTemplateVersionRequested.type,
    activateTemplateVersionWorker,
  );
  yield takeEvery(
    templatesActions.fetchTemplatePreviewRequested.type,
    fetchTemplatePreviewWorker
  );
  yield takeEvery(
    templatesActions.getTemplatePresignedUrlRequested.type,
    getTemplatePresignedUrlWorker,
  );
}